import type { WorkflowDefinition } from "../types";

/**
 * W23 — Optimize a page for a target grounding search.
 *
 * The tool-shaped playbook: four of its five steps live inside one wizard, and
 * three people optimizing three pages in the same week is the normal case. So
 * it is `perTarget` on the pipeline run — the runner's CTA carries
 * `?workflowRun=<runId>` and the optimizer binds the run it creates.
 *
 * Every `since` predicate is `scope: "target"`, which means they read the bound
 * run's chain (the run itself plus anything with `parentRunId` pointing at it)
 * and are false until the run is bound. That is deliberate: before binding, a
 * colleague scoring their own page must not check off this run's steps.
 */
export const fanoutOptimizerWorkflow: WorkflowDefinition = {
	id: "fanout-optimizer",
	wNumber: 23,
	title: "Win a target AI grounding search",
	outcome:
		"Win the search AI makes before it answers, then watch the rank move.",
	audiences: ["content", "inhouse", "agencies"],
	uses: ["Citation Optimizer", "AI Visibility Rankings", "Projects"],
	scope: "property",
	concurrency: "perTarget",
	progress: "explicit",
	target: {
		kind: "pipelineRun",
		label: "Page and query",
		pipelineMode: "tracked",
	},
	isApplicable: (mode) => mode.showGroundingSearches,
	articleSlug: "how-to-win-a-target-ai-grounding-search",
	articleStatus: "published",
	status: "live",
	steps: [
		{
			id: "pick-query",
			title: "Pick the search you want to win",
			page: "Citation Optimizer",
			route: "/citation-optimizer",
			criterion: "You've scored a tracked search for this workflow.",
			why: "Grounding searches are what AI actually looks up before answering. Winning one moves real answers.",
			docsHref: "/docs/dashboards/citation-optimizer",
			tasks: [
				"Browse tracked searches and pick one with high impact",
				"Check who holds the rank today",
				"Or type a keyword if the search is not tracked yet",
			],
			enrichment: "seoGaps",
			complete: {
				kind: "all",
				of: [
					{
						kind: "since",
						event: "pipelineRunCreated",
						scope: "target",
					},
					{ kind: "count", key: "pipeline.tracked", gte: 1 },
				],
			},
		},
		{
			id: "pick-source",
			title: "Choose the page you will improve",
			page: "Citation Optimizer",
			route: "/citation-optimizer",
			criterion:
				"You've matched one page to the search with no overlap flags.",
			why: "The score is one page against one search. If two of your pages match it closely they compete with each other.",
			docsHref: "/docs/dashboards/citation-optimizer",
			tasks: [
				"Pick the suggested page, paste a URL, or paste a draft",
				"Clear any overlapping-pages flag first",
			],
			complete: {
				kind: "all",
				of: [
					{
						kind: "since",
						event: "pipelineRunCreated",
						scope: "target",
					},
					{ kind: "count", key: "pipeline.tracked", gte: 1 },
				],
			},
		},
		{
			id: "score",
			title: "Score the page against the search",
			page: "Citation Optimizer",
			route: "/citation-optimizer",
			criterion: "You've completed a score for this workflow.",
			why: "The score is a diagnosis, not a guess. Each check shows what specifically stops the page being cited.",
			docsHref: "/docs/methodology/citation-optimizer",
			tasks: [
				"Score the page and read the stoplight strip",
				"Open the failing checks for specific advice",
			],
			complete: {
				kind: "since",
				event: "pipelineRunCompleted",
				scope: "target",
			},
			spend: {
				kind: "free",
				note: "Scoring is free. Only a rewrite costs credits.",
			},
			signal: ({ state }) => {
				const score =
					state.target?.pipeline?.latestScore ??
					state.property?.pipeline.latestScore ??
					null;
				if (score === null) {
					return null;
				}
				return {
					kind: "score",
					label: "Citation score",
					value: score,
					target: 70,
				};
			},
		},
		{
			id: "revise",
			title: "Rewrite and export the page",
			page: "Citation Optimizer",
			route: "/citation-optimizer",
			criterion: "You've generated a rewrite for this workflow.",
			why: "The rewrite fixes what failed while keeping your voice. Scoring again is free, so keep going until the page is ready.",
			docsHref: "/docs/dashboards/citation-optimizer",
			tasks: [
				"Generate the rewrite and review the diff",
				"Score again and repeat until the banner clears",
				"Export and publish through your CMS",
			],
			complete: {
				kind: "since",
				event: "pipelineRevisionCreated",
				scope: "target",
			},
			spend: { kind: "citationRewrite" },
			signal: ({ state }) => {
				const revisions = state.target?.pipeline?.revisionCount ?? null;
				if (revisions === null) {
					return null;
				}
				return {
					kind: "count",
					label: "Rewrites generated",
					value: revisions,
					target: 1,
				};
			},
		},
		{
			id: "track",
			title: "Publish it and watch the rank",
			page: "AI Visibility Rankings",
			route: "/ai-visibility-rankings",
			criterion: "You've got an SEO project tracking the page.",
			why: "This is the payoff check. Once the page is recrawled, the rank tells you whether the work landed.",
			docsHref: "/docs/dashboards/ai-visibility-rankings",
			tasks: [
				"Publish, then confirm the page was recrawled",
				"Watch the rank and note the publish date",
				"Add the page group to an SEO project",
			],
			complete: { kind: "count", key: "projectTypes.SEO", gte: 1 },
			enrichment: "rankTrend",
			// Rank history is not in the state loader; the step panel hydrates
			// it from the SEO-opportunities hook rather than inventing a series.
			signal: () => null,
		},
	],
};
